import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule, MatTabChangeEvent } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { JobRequestsService } from '../../../services/job-requests.service';
import { DesignService } from '../../../services/design.service';
import { JobRequestOverviewDto, OverviewGroupDto } from '@models/job-request-overview-dto.interface';
import { JobRequestIterationsDto, DesignIterationDto } from '@models/job-request-iterations-dto.interface';
import { DesignSummaryDto, JobRequestDesignSummaryDto } from '@models/job-request-summary-dto.interface';
import { AttachmentDto } from '@models/job-detail-dto.interface';
import { AttachmentService } from '../../../services/attachment.service';
import { AttachmentType } from '../../../enums/attachment-type.enum';
import { JobRequestOverviewComponent } from '../job-request-overview/job-request-overview.component';
import { FormulationIterationsComponent } from './formulation-iterations/formulation-iterations.component';
import { TestResultsComponent } from './test-results/test-results.component';
import { DesignSummaryComponent } from './design-summary/design-summary.component';
import { BrineFormulationComponent } from './brine-formulation/brine-formulation.component';
import { MudFormulationComponent } from './mud-formulation/mud-formulation.component';
import { AddMudCheckMaterialDialogComponent } from './add-mud-check-material-dialog/add-mud-check-material-dialog.component';

@Component({
  selector: 'app-job-request-details',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    RouterLink,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    JobRequestOverviewComponent,
    FormulationIterationsComponent,
    TestResultsComponent,
    DesignSummaryComponent,
    BrineFormulationComponent,
    MudFormulationComponent,
  ],
  templateUrl: './job-request-details.component.html',
  styleUrl: './job-request-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestDetailsComponent implements OnInit {
  private static readonly DENSITY_CHANGE_JOB_REQUEST_TYPE_ID = 2;
  private static readonly PILOT_TEST_JOB_REQUEST_TYPE_ID = 3;

  private readonly route = inject(ActivatedRoute);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly designService = inject(DesignService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly dialog = inject(MatDialog);
  private static readonly MUD_CHECK_JOB_REQUEST_TYPE_ID = 1;

  protected readonly AttachmentType = AttachmentType;

  protected overview = signal<JobRequestOverviewDto | null>(null);
  protected iterations = signal<JobRequestIterationsDto | null>(null);
  protected isLoading = signal(false);
  protected isLoadingIterations = signal(false);
  protected iterationsLoaded = signal(false);
  protected jobId = signal<number | null>(null);
  protected selectedTabIndex = signal(0);
  protected activeTestDesignId = signal<number | null>(null);
  protected activeTestDesignName = signal<string>('');
  protected activeTestIsEditable = signal(false);
  protected activeTestOverviewGroups = signal<OverviewGroupDto[]>([]);
  protected isLoadingTestOverview = signal(false);
  protected summary = signal<JobRequestDesignSummaryDto | null>(null);
  protected summaryLoaded = signal(false);
  protected isLoadingSummary = signal(false);
  protected activeSummaryDesignId = signal<number | null>(null);
  protected brineDensityPpg = signal<number | null>(null);
  protected brineVolumeMl = signal<number | null>(null);
  private currentJobRequestId: number | null = null;

  @ViewChild('fileInput')
  private fileInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const jobId = Number(params.get('jobId'));
      const id = Number(params.get('id'));
      this.jobId.set(jobId || null);
      this.currentJobRequestId = id || null;
      this.iterationsLoaded.set(false);
      this.iterations.set(null);
      this.summaryLoaded.set(false);
      this.summary.set(null);
      this.activeSummaryDesignId.set(null);
      if (id) {
        this.loadOverview(id);
      }
    });
  }

  protected onTabChange(event: MatTabChangeEvent): void {
    const previousTabIndex = this.selectedTabIndex();
    this.selectedTabIndex.set(event.index);
    // Reload iterations when returning from Tests so the results panel gets fresh data.
    if (event.tab.textLabel === 'Formulation Iterations'
        && this.currentJobRequestId
        && (!this.iterationsLoaded() || previousTabIndex === 1)) {
      this.loadIterations(this.currentJobRequestId);
    }

    if (event.tab.textLabel === 'Summary' && this.currentJobRequestId && !this.summaryLoaded()) {
      this.loadSummary(this.currentJobRequestId);
    }
  }

  protected activeSummary(): DesignSummaryDto | null {
    const designs = this.summary()?.designs ?? [];
    if (designs.length === 0) return null;
    if (!this.isPilotTestJob()) return designs[0];

    const activeId = this.activeSummaryDesignId();
    return designs.find(d => d.designId === activeId) ?? designs[0];
  }

  protected onSelectSummaryDesign(designId: number): void {
    this.activeSummaryDesignId.set(designId);
  }

  protected onBrineDensityResolved(density: number | null): void {
    this.brineDensityPpg.set(density);
  }

  protected onMudBrineVolumeChanged(volumeMl: number | null): void {
    this.brineVolumeMl.set(volumeMl);
  }

  private loadSummary(id: number): void {
    this.isLoadingSummary.set(true);
    this.jobRequestsService.getSummary(id).subscribe({
      next: data => {
        this.summary.set(data);
        this.activeSummaryDesignId.set(data.designs[0]?.designId ?? null);
        this.summaryLoaded.set(true);
        this.isLoadingSummary.set(false);
      },
      error: () => {
        this.summaryLoaded.set(true);
        this.isLoadingSummary.set(false);
      }
    });
  }

  protected isPilotTestJob(): boolean {
    return this.overview()?.jobRequestTypeId === JobRequestDetailsComponent.PILOT_TEST_JOB_REQUEST_TYPE_ID;
  }

  protected isIterationJob(): boolean {
    const typeId = this.overview()?.jobRequestTypeId;
    return typeId === JobRequestDetailsComponent.PILOT_TEST_JOB_REQUEST_TYPE_ID
      || typeId === JobRequestDetailsComponent.DENSITY_CHANGE_JOB_REQUEST_TYPE_ID;
  }

  protected requestTypeLabel(): string {
    const typeId = this.overview()?.jobRequestTypeId;
    if (typeId === 1) return 'Mud Check';
    if (typeId === JobRequestDetailsComponent.DENSITY_CHANGE_JOB_REQUEST_TYPE_ID) return 'Cutback/Weigh Up';
    if (typeId === JobRequestDetailsComponent.PILOT_TEST_JOB_REQUEST_TYPE_ID) return 'Pilot/Comparison Test';
    if (typeId === 4) return 'Material As Received';
    return 'Job Request';
  }

  protected formatSignedDensityDelta(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
  }

  protected canAddMudCheckMaterial(): boolean {
    const data = this.overview();
    return !!data
      && data.jobRequestTypeId === JobRequestDetailsComponent.MUD_CHECK_JOB_REQUEST_TYPE_ID
      && data.materialId == null
      && data.isEditable;
  }

  protected onAddMudCheckMaterial(): void {
    const data = this.overview();
    if (!data) return;

    this.dialog
      .open(AddMudCheckMaterialDialogComponent, {
        width: '650px',
        minWidth: '650px',
        maxHeight: '90vh',
        data: { jobRequestId: data.id },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) this.reload();
      });
  }

  protected onIterationsChanged(): void {
    this.summaryLoaded.set(false);
    if (this.currentJobRequestId) {
      this.loadIterations(this.currentJobRequestId);
    }
  }

  protected onTestDesignSelected(event: { designId: number; designName: string; isEditable: boolean } | null): void {
    if (!event) {
      this.activeTestDesignId.set(null);
      this.activeTestDesignName.set('');
      this.activeTestOverviewGroups.set([]);
      return;
    }
    this.activeTestDesignId.set(event.designId);
    this.activeTestDesignName.set(event.designName);
    this.activeTestIsEditable.set(event.isEditable);
    this.loadTestOverview(event.designId);
    // Switch to Tests tab (index 1 for pilot test jobs)
    this.selectedTabIndex.set(1);
  }

  protected onSelectTestDesign(design: DesignIterationDto, index: number): void {
    const name = design.name?.trim() || `Iteration ${index + 1}`;
    const isEditable = !!(this.iterations()?.isEditable && design.isEditable);
    this.activeTestDesignId.set(design.id);
    this.activeTestDesignName.set(name);
    this.activeTestIsEditable.set(isEditable);
    this.loadTestOverview(design.id);
  }

  protected onTestsChangedFromPanel(): void {
    this.summaryLoaded.set(false);
    const id = this.activeTestDesignId();
    if (id !== null) {
      this.loadTestOverview(id);
    }
    if (this.currentJobRequestId) {
      this.loadIterations(this.currentJobRequestId);
    }
  }

  private loadTestOverview(designId: number): void {
    this.isLoadingTestOverview.set(true);
    this.designService.getDesignTestOverview(designId).subscribe({
      next: groups => {
        this.activeTestOverviewGroups.set([{ title: 'Tests', overviews: groups }]);
        this.isLoadingTestOverview.set(false);
      },
      error: () => {
        this.activeTestOverviewGroups.set([{ title: 'Tests', overviews: [] }]);
        this.isLoadingTestOverview.set(false);
      },
    });
  }

  private loadOverview(id: number): void {
    this.isLoading.set(true);
    this.jobRequestsService.getOverview(id).subscribe({
      next: data => {
        this.overview.set(data);
        this.isLoading.set(false);
        if (this.isIterationJob()
            && !this.iterationsLoaded()) {
          this.loadIterations(id);
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  private loadIterations(id: number): void {
    this.isLoadingIterations.set(true);
    this.designService.getIterations(id).subscribe({
      next: data => {
        this.iterations.set(data);
        this.iterationsLoaded.set(true);
        this.isLoadingIterations.set(false);
      },
      error: () => {
        this.iterationsLoaded.set(true);
        this.isLoadingIterations.set(false);
      }
    });
  }

  protected reload(): void {
    const current = this.overview();
    if (current) this.loadOverview(current.id);
  }

  protected onAddAttachment(): void {
    this.fileInput.nativeElement.click();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const current = this.overview();
    if (!current) return;

    this.attachmentService.upload(current.id, AttachmentType.JobRequest, file).subscribe({
      next: () => this.reload(),
      error: err => console.error('Upload failed', err)
    });

    input.value = '';
  }

  protected onOpenAttachment(att: AttachmentDto): void {
    this.attachmentService.download(att.id, AttachmentType.JobRequest).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = att.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      error: err => console.error('Download failed', err)
    });
  }

  protected onDeleteAttachment(att: AttachmentDto): void {
    this.attachmentService.delete(att.id, AttachmentType.JobRequest).subscribe({
      next: () => this.reload(),
      error: err => console.error('Delete failed', err)
    });
  }
}
